import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { useQuery } from "@apollo/client"
import { DataLoading, Theme } from "@pga/pga-component-library"

import CurriculumTracker from "../CurriculumTracker"
import { ButtonFw } from "../../Button"
import { urlFor, getCurrentProgressLevelCode } from "./CurriculumMessage/utils"
import CurriculumMessage from "./CurriculumMessage"
import { useMembershipJourneyContext } from "../MembershipJourney/MembershipJourneyContext"
import { resolveAcceptableProgressStanding } from "../../../utils/resolveAcceptableProgressStanding"
import { shouldSuppressAdvancementCtas } from "../../../utils/shouldSuppressAdvancementCtas"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import { isAffiliate } from "../../utils"
import { COACH_TOOLS_API } from "../../../apolloClient"
import { redirectToMemberApplications } from "../../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../../queries/associateApplicationEligibility"
import BecomeAssociateModal from "./BecomeAssociateModal"

const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 5px 20px 0 20px;
  width: 100%;
  height: 100%;
  border-right: 2px solid ${Theme.colors.darkgrey};

  @media (max-width: 767px) {
    padding-top: 35px;
  }
`

export const ButtonsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`

const LevelTrackBox = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
`

export const TextWrapper = styled.div`
  padding: 0 15px;
`

export const Loading = () => (
  <Box>
    <br />
    <DataLoading h={25} />
    <br />
    <DataLoading h={60} />
    <ButtonsBox>
      <DataLoading h={40} w={150} />
      <DataLoading h={40} w={150} />
    </ButtonsBox>
  </Box>
)

const CourseCurriculum = () => {
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)
  const { me, pgmProgress } = useMembershipJourneyContext()
  const affiliateViewer = isAffiliate(me)
  const [isTerminatedNoticeEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_TERMINATED_NOTICE]) ?? []
  const progressStanding = resolveAcceptableProgressStanding({
    me,
    pgmProgress,
  })
  const suppressLegacyBecomeAssociate = shouldSuppressAdvancementCtas({
    standing: progressStanding,
    isTerminatedNoticeEnabled,
  })

  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(
    ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY,
    {
      context: { api: COACH_TOOLS_API },
      errorPolicy: "all",
      skip: !affiliateViewer || suppressLegacyBecomeAssociate,
    },
  )

  const showBecomeAssociateButton =
    !suppressLegacyBecomeAssociate &&
    affiliateViewer &&
    !eligibilityLoading &&
    eligibilityData?.associateApplicationEligibility?.eligible === true

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  const current = getCurrentProgressLevelCode(me)
  const transcriptUrl = urlFor("transcript", me)
  const portalUrl = urlFor("portal", me)
  return (
    <Box>
      <TextWrapper>{CurriculumMessage(me)}</TextWrapper>
      <LevelTrackBox>
        <CurriculumTracker current={current} me={me} />
      </LevelTrackBox>
      <ButtonsBox>
        {showBecomeAssociateButton && (
          <div className="w-100 p-3">
            <ButtonFw onClick={() => setIsBecomeAssociateModalOpen(true)}>
              Become Associate
            </ButtonFw>
          </div>
        )}
        {transcriptUrl && (
          <a
            className="w-100 p-3"
            href={transcriptUrl}
            rel="noopener noreferrer"
          >
            <ButtonFw outline>VIEW TRANSCRIPT</ButtonFw>
          </a>
        )}
        {portalUrl && (
          <a
            className="w-100 p-3"
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ButtonFw>EDUCATION PORTAL</ButtonFw>
          </a>
        )}
      </ButtonsBox>
      <BecomeAssociateModal
        isOpen={isBecomeAssociateModalOpen}
        onClose={() => setIsBecomeAssociateModalOpen(false)}
        onApply={redirectToMemberApplications}
      />
    </Box>
  )
}

export default CourseCurriculum
